import { useSelector } from 'react-redux';

import { SubscriptionIntervalTypes } from '../../utils/constants';
import { getFableticsEntitlement, isUserFableticsEligible, userHasGlobalPrivileges } from '../../utils/user';
import { useStatsig } from './useStatsig';

const VARIANT_PARAMETER = 'ui_variant';
const DEFAULT_VARIANT = 'control';

// Single source of truth for the Fabletics module. Returns the gate decision
// (enabled), the assigned experiment variant, and whether the user is excluded
// from the experiment in one call. The hook short-circuits before touching
// Statsig's experiment API for users who shouldn't be in the experiment, so no
// experiment exposure is logged for them.
//
// Audience routing:
// - Fabletics-entitled (any plan with ctaUrl): control, excluded — entitlement
//   overrides country and tier.
// - Annual subscribers: country-routed. US annual renders the Fabletics
//   control banner (entitled dual-card if their plans carry GP, else the new
//   single-card GP layout). Non-US annual falls back to `GlobalPrivilegesBanner`.
// - Non-annual users with a GP plan: control, excluded — they render the
//   single-card GP layout regardless of country.
// - US non-annual without any plan: in experiment, gets a creative variant.
// - Anyone else: enabled=false (consumers fall back to GlobalPrivilegesBanner).

const useFabletics = () => {
    const user = useSelector(({ auth }) => auth?.user);
    const { checkGate, getExperiment, featureGates, experiments } = useStatsig();

    if (!checkGate(featureGates.FABLETICS_POWER_BENEFIT)) {
        return { enabled: false, variant: DEFAULT_VARIANT, isExperimentExcluded: false };
    }

    if (getFableticsEntitlement(user)?.ctaUrl) {
        return { enabled: true, variant: DEFAULT_VARIANT, isExperimentExcluded: true };
    }

    const isAnnualSubscriber = !!user?.isPowered &&
        user?.subscriptionTierType === SubscriptionIntervalTypes.YEAR;
    const isUSEligible = isUserFableticsEligible(user);

    if (isAnnualSubscriber) {
        return isUSEligible
            ? { enabled: true, variant: DEFAULT_VARIANT, isExperimentExcluded: true }
            : { enabled: false, variant: DEFAULT_VARIANT, isExperimentExcluded: false };
    }

    if (userHasGlobalPrivileges(user)) {
        return { enabled: true, variant: DEFAULT_VARIANT, isExperimentExcluded: true };
    }

    if (!isUSEligible) {
        return { enabled: false, variant: DEFAULT_VARIANT, isExperimentExcluded: false };
    }

    const experiment = getExperiment(experiments.FABLETICS_CREATIVE);
    const variant = experiment.get(VARIANT_PARAMETER, DEFAULT_VARIANT);
    return { enabled: true, variant, isExperimentExcluded: false };
};

export default useFabletics;
